import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MdCheckCircle,
  MdEmail,
  MdLink,
  MdMenuBook,
  MdPeople,
  MdPerson,
  MdPersonAdd,
} from "react-icons/md";
import Avatar from "components/avatar";
import Badge from "components/badge/Badge";
import Button from "components/button";
import Card from "components/card";
import DataTable from "components/data-table";
import { EmptyState, ErrorState } from "components/states";
import { getApiErrorMessage } from "api";
import { EMPTY, formatDateTime } from "utils/format";
import { getRespondent, profilePath } from "./respondent";

const PAGINATION = { pageSize: 25, pageSizeOptions: [25, 50, 100] };

const DevotionalCell = ({ response }) => {
  if (response.devotionalSubscription) {
    const sub = response.devotionalSubscription;
    return (
      <Badge tone="success">
        <MdMenuBook aria-hidden="true" className="h-3 w-3" />
        Day {sub.lastSentDay || Math.max(1, sub.currentDay - 1)}/30
      </Badge>
    );
  }
  if (response.followUpRequested) {
    return (
      <Badge tone="warning">
        <MdEmail aria-hidden="true" className="h-3 w-3" />
        Requested
      </Badge>
    );
  }
  return <span className="text-sm text-fg-muted">{EMPTY}</span>;
};

const StatusCell = ({ respondent }) => {
  if (respondent.hasLinkedUser) {
    return (
      <Badge tone="success">
        <MdCheckCircle aria-hidden="true" className="h-3 w-3" />
        In Database
      </Badge>
    );
  }
  if (respondent.hasFollowUpInfo) {
    return (
      <Badge tone="warning">
        <MdPerson aria-hidden="true" className="h-3 w-3" />
        Contact Only
      </Badge>
    );
  }
  return <Badge tone="neutral">Anonymous</Badge>;
};

const ResponsesTab = ({ responses, loading, error, onRetry, addingPersonId, onAddPerson, onView }) => {
  // Cells read the latest handlers/busy row at render time, so the column
  // definitions stay stable and paging is not reset on every parent render.
  const ctx = useRef({});
  ctx.current = { addingPersonId, onAddPerson, onView };

  const columns = useMemo(
    () => [
      {
        id: "respondent",
        header: "Respondent",
        cell: ({ row }) => {
          const response = row.original;
          const respondent = getRespondent(response);
          return (
            <div className="flex items-center gap-3">
              <Avatar
                user={respondent.hasLinkedUser ? response.user : undefined}
                name={respondent.displayName}
                size="md"
                noBorder
              />
              <div className="min-w-0">
                {respondent.hasLinkedUser ? (
                  <Link
                    to={profilePath(response)}
                    className="focus-ring rounded-sm font-medium text-primary hover:underline dark:text-accent"
                  >
                    {respondent.displayName}
                  </Link>
                ) : (
                  <p className="font-medium text-fg">{respondent.displayName}</p>
                )}
                {respondent.displayEmail && (
                  <p className="text-sm text-fg-muted">{respondent.displayEmail}</p>
                )}
                {response.userMatched && respondent.hasLinkedUser && (
                  <Badge tone="info" size="sm" className="mt-1">
                    <MdLink aria-hidden="true" className="h-3 w-3" />
                    Auto-matched
                  </Badge>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "submitted",
        header: "Submitted",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-fg">
            {formatDateTime(row.original.submittedAt || row.original.createdAt)}
          </span>
        ),
      },
      {
        id: "devotional",
        header: "Devotional",
        cell: ({ row }) => <DevotionalCell response={row.original} />,
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <StatusCell respondent={getRespondent(row.original)} />,
      },
      {
        id: "actions",
        header: "Actions",
        meta: { align: "right" },
        cell: ({ row }) => {
          const response = row.original;
          const respondent = getRespondent(response);
          const { addingPersonId: busyId, onAddPerson: add, onView: view } = ctx.current;
          return (
            <div className="flex items-center justify-end gap-2">
              {respondent.hasFollowUpInfo && !respondent.hasLinkedUser && (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={busyId === response.id}
                  leftIcon={<MdPersonAdd aria-hidden="true" />}
                  onClick={() => add(response.id)}
                  aria-label={`Add to People: ${respondent.displayName}`}
                >
                  Add to People
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => view(response)}
                aria-label={`View response from ${respondent.displayName}`}
              >
                View
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  if (error) {
    return (
      <Card padding="md">
        <ErrorState
          message={getApiErrorMessage(error, "Responses could not be loaded.")}
          onRetry={onRetry}
        />
      </Card>
    );
  }

  if (!loading && responses.length === 0) {
    return (
      <Card padding="md">
        <EmptyState
          icon={MdPeople}
          title="No Responses Yet"
          description="Responses will appear here once guests complete the survey."
        />
      </Card>
    );
  }

  return (
    <DataTable
      aria-label="Survey responses"
      columns={columns}
      data={responses}
      loading={loading}
      getRowId={(response) => String(response.id)}
      pagination={PAGINATION}
    />
  );
};

export default ResponsesTab;
